// src/routes/BuilderRoutes/BuilderRoutes.jsx
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import useBuilderStatusStore from "../../store/builders/useBuilderStatusStore";
import useAuthStore from "../../store/useAuthStore";
import { getBuilderHomePath } from "../../utils/builderNavigation";
import Dashboard from "../../pages/Builders/Dashboard";
import ProjectLive from "../../pages/Builders/ProjectLive";
import ProjectInvite from "../../pages/Builders/ProjectInvite";
import Services from "../../pages/Builders/Services";
import Portfolio from "../../pages/Builders/Portfolio";
import ProjectHold from "../../pages/Builders/ProjectHold";
import ProjectRejection from "../../pages/Builders/ProjectRecjection";
import CompletionRequest from "../../pages/Builders/CompletionRequest";
import ProjectCompleted from "../../pages/Builders/CompletedProject";
import Notification from "../../pages/Builders/Notification";
import ProjectEnquiry from "../../pages/Builders/ProjectEnquiry";
import SubscriptionHistory from "../../pages/Builders/SubscriptionHistory";
import Subscription from "../../pages/Builders/Subscription";
import ProfileCard from "../../pages/Builders/ProfileCard";
import Payment from "../../pages/Builders/Payment"; // Ensure this is correct
import Profile from "../../pages/Builders/Profile";
import Support from "../../pages/Builders/Support";
import Signuppage from "../../pages/Signuppage";
import Jobs from "../../pages/Builders/Jobs.jsx"; // MODIFIED: Import the new Jobs component
import ProjectDetail from "../../pages/Builders/ProjectDetail";
import ProjectManagement from "../../pages/Builders/ProjectManagement";
import JobProjectManagement from "../../pages/Builders/JobProjectManagement";
import HousingPlot from "../../pages/Builders/HousingPlot";
import HousingPlotPage from "../../pages/Builders/HousingPlotPage";
import PlotPage from "../../pages/Builders/PlotPage";
import PropertyPost from "../../pages/Builders/PropertyPost.jsx";
import CustomerEnquiry from "../../pages/Builders/CustomerEnquiry.jsx";
import TransactionHistory from "../../pages/Builders/TransactionHistory.jsx";
import RealEstateLayout from "../../pages/Builders/RealEstateLayout";
import ConsultantProfiles from "../../pages/Builders/ConsultantProfiles";
import ConsultantDetail from "../../pages/Builders/ConsultantDetail";
import ConsultantSubscription from "../../pages/Builders/ConsultantSubscription";
import BuilderConsultantProfile from "../../pages/Builders/BuilderConsultantProfile";
import BuilderConsultantSubscription from "../../pages/Builders/BuilderConsultantSubscription";
import BuilderConsultantSubscriptionHistory from "../../pages/Builders/BuilderConsultantSubscriptionHistory";
import ConsultantManagement from "../../pages/Builders/ConsultantManagement";
import ERPManagement from "../../pages/Builders/ERPManagement";
import ProjectIncharge from "../../pages/Builders/ProjectIncharge";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { status } = useBuilderStatusStore();
  const { user } = useAuthStore();

  // Redirect to dashboard if status is restricted
  const restrictedStatuses = ["Pending_Verification", "Rejected", "On_hold"];

  if (restrictedStatuses.includes(status)) {
    return <Navigate to={getBuilderHomePath(user)} replace />;
  }

  return children;
};

const InchargeAwareDashboard = () => {
  const { user } = useAuthStore();
  if (user?.role === "incharge") {
    return <Navigate to="/builder/projects" replace />;
  }
  return <Dashboard />;
};

const InchargeAwareProfile = () => {
  const { user } = useAuthStore();
  if (user?.role === "incharge") {
    return <Navigate to="/builder/projects" replace />;
  }
  return <Profile />;
};

const BuilderRoutes = () => {
  return (
    <Routes>
      {/* Dashboard - Always accessible */}
      <Route path="/dashboard" element={<InchargeAwareDashboard />} />

      {/* Subscription routes - Accessible for Approved status */}
      <Route path="/subscription" element={<Subscription />} />
      <Route path="/subscriptionhistory" element={<SubscriptionHistory />} />

      {/* Protected routes - Only for Active/Approved with subscription */}
      <Route path="/projects" element={<ProtectedRoute><ProjectManagement /></ProtectedRoute>} />
      <Route path="/liveproject" element={<Navigate to="/builder/projects?tab=live" replace />} />
      <Route path="/projectservices" element={<ProtectedRoute><Services /></ProtectedRoute>} />
      <Route path="/projectinvite" element={<ProtectedRoute><ProjectInvite /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
      <Route path="/holdproject" element={<Navigate to="/builder/projects?tab=hold" replace />} />
      <Route path="/rejectionproject" element={<Navigate to="/builder/projects?tab=rejected" replace />} />
      <Route path="/completionrequest" element={<Navigate to="/builder/projects?tab=completion_request" replace />} />
      <Route path="/completedproject" element={<Navigate to="/builder/projects?tab=completed" replace />} />
      <Route path="/project/:projectId/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
      <Route path="/project/:projectId/detail" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
      <Route path="/notification" element={<ProtectedRoute><Notification /></ProtectedRoute>} />
      <Route path="/projectenquiry" element={<ProtectedRoute><ProjectEnquiry /></ProtectedRoute>} />
      <Route path="/profilecard" element={<ProtectedRoute><ProfileCard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><InchargeAwareProfile /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/signup" element={<Signuppage />} />
      <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
      <Route path="/jobprojectmanagement" element={<ProtectedRoute><JobProjectManagement /></ProtectedRoute>} />
      <Route path="/customerenquiry" element={<ProtectedRoute><CustomerEnquiry /></ProtectedRoute>} />
      <Route path="/real-estate" element={<ProtectedRoute><RealEstateLayout /></ProtectedRoute>} />
      <Route path="/housing-plot" element={<ProtectedRoute><HousingPlotPage /></ProtectedRoute>} />
      <Route path="/housingplot" element={<Navigate to="/builder/housing-plot" replace />} />
      <Route path="/plot" element={<ProtectedRoute><PlotPage /></ProtectedRoute>} />

      {/* Consultant Profile Routes - View Other Consultants */}
      <Route path="/consultant-profiles" element={<ProtectedRoute><ConsultantProfiles /></ProtectedRoute>} />
      <Route path="/consultant-detail/:consultantId" element={<ProtectedRoute><ConsultantDetail /></ProtectedRoute>} />
      <Route path="/consultant-subscription/:consultantId" element={<ProtectedRoute><ConsultantSubscription /></ProtectedRoute>} />

      {/* Builder's Own Consultant Profile Routes - Unified Management */}
      <Route path="/consultant" element={<ProtectedRoute><ConsultantManagement /></ProtectedRoute>} />

      {/* ERP Management */}
      <Route path="/erp" element={<ProtectedRoute><ERPManagement /></ProtectedRoute>} />

      {/* Project Incharge Management */}
      <Route path="/project-incharge" element={<ProtectedRoute><ProjectIncharge /></ProtectedRoute>} />

      {/* Redirections for backward compatibility */}
      <Route path="/consultant-profile-create" element={<Navigate to="/builder/consultant?tab=profile" replace />} />
      <Route path="/consultant-profile-subscription" element={<Navigate to="/builder/consultant?tab=subscription" replace />} />
      <Route path="/consultant-profile-subscription-history" element={<Navigate to="/builder/consultant?tab=history" replace />} />
    </Routes>
  );
};

export default BuilderRoutes;
